/**
 * IR-661 Phase 7 Task 12 — Account sub-form (social_media_account / account).
 *
 * Backend reality (Task 5, api/ops/serializers_data.py:247-283):
 *   - AccountBasicInfoUpdateSerializer: bio, title, country (TEXT — NOT
 *     FK!), country_code, phone_number, profile_url, is_verified (free
 *     text!), location.  It also exposes `platform` as a ChoiceField for
 *     validation only — it writes nowhere. NO username, follower_count,
 *     verified-as-bool, display_name on AccountBasicInfo.
 *   - AccountAdditionalInfoUpdateSerializer: account_verified (Boolean),
 *     followers (Decimal max_digits=5,decimal_places=2 → max 999.99),
 *     following (max 99,999,999.99), friends (max 999.99), language,
 *     banner_url.
 *
 *   Entity.app_type is the platform anchor and is in
 *   `_FORBIDDEN_EDIT_FIELDS` server-side. The FE displays it read-only
 *   and never lets the user edit it (would orphan appdata rows).
 */

export interface AccountFormState {
  // AccountBasicInfo fields
  bio: string;
  title: string;
  country: string;
  // AccountAdditionalInfo fields
  account_verified: boolean | null;
  followers: string; // string-typed so the input round-trips empty / decimal
}

export function initialAccount(seed: Record<string, unknown>): AccountFormState {
  return {
    bio: typeof seed.bio === "string" ? seed.bio : "",
    title: typeof seed.title === "string" ? seed.title : "",
    country: typeof seed.country === "string" ? seed.country : "",
    account_verified: typeof seed.account_verified === "boolean" ? seed.account_verified : null,
    followers:
      typeof seed.followers === "string"
        ? seed.followers
        : typeof seed.followers === "number"
          ? String(seed.followers)
          : "",
  };
}

export function diffAccount(
  form: AccountFormState,
  seed: Record<string, unknown>,
): Record<string, unknown> {
  const initial = initialAccount(seed);
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(form) as Array<keyof AccountFormState>) {
    if (form[key] !== initial[key]) {
      out[key] = form[key];
    }
  }
  return out;
}

interface Props {
  form: AccountFormState;
  setForm: (next: AccountFormState) => void;
  /** Read-only display of Entity.app_type. Never editable here. */
  appType: string | null | undefined;
}

export function AccountForm({ form, setForm, appType }: Props) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Read-only platform display — Entity.app_type is in
          _FORBIDDEN_EDIT_FIELDS server-side. */}
      <div>
        <div style={{ fontSize: 12, color: "var(--iris-fg-3)" }}>Platform (read-only)</div>
        <div
          style={{
            marginTop: 4,
            fontFamily: "var(--iris-font-mono)",
            fontSize: 12,
            color: "var(--iris-fg-2)",
            padding: "6px 10px",
            border: "1px solid var(--iris-border-2)",
            borderRadius: 6,
            background: "var(--iris-bg-2)",
          }}
        >
          {appType || "—"}
        </div>
      </div>

      <label
        htmlFor="ir661-edit-entity-acc-bio"
        style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
      >
        Bio
        <input
          id="ir661-edit-entity-acc-bio"
          className="iris-input"
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
          style={{ width: "100%", marginTop: 4 }}
        />
      </label>

      <label
        htmlFor="ir661-edit-entity-acc-title"
        style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
      >
        Title
        <input
          id="ir661-edit-entity-acc-title"
          className="iris-input"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          style={{ width: "100%", marginTop: 4 }}
        />
      </label>

      <label
        htmlFor="ir661-edit-entity-acc-country"
        style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
      >
        Country
        <input
          id="ir661-edit-entity-acc-country"
          className="iris-input"
          value={form.country}
          onChange={(e) => setForm({ ...form, country: e.target.value })}
          style={{ width: "100%", marginTop: 4 }}
        />
      </label>

      <label
        htmlFor="ir661-edit-entity-acc-verified"
        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}
      >
        <input
          id="ir661-edit-entity-acc-verified"
          type="checkbox"
          checked={form.account_verified === true}
          onChange={(e) => setForm({ ...form, account_verified: e.target.checked })}
        />
        Verified
      </label>

      <label
        htmlFor="ir661-edit-entity-acc-followers"
        style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
      >
        Followers
        <input
          id="ir661-edit-entity-acc-followers"
          type="number"
          min={0}
          max={999.99}
          step="0.01"
          className="iris-input"
          value={form.followers}
          onChange={(e) => setForm({ ...form, followers: e.target.value })}
          style={{ width: "100%", marginTop: 4 }}
        />
      </label>
    </div>
  );
}
